import React, { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { NavLink } from "./NavLink";
import { MobileMenu } from "./MobileMenu";
import { useModal } from "../../lib/ModalContext";
import { useScrolledPast } from "../../lib/useScrolledPast";
import { EASE } from "../../lib/motion";

const wordmarkProps = (brand) => ({
    logoUrl: brand?.logo_url,
    brandName: brand?.name,
    wordmark: brand?.wordmark,
    logoSize: brand?.logo_size,
    nameColor: brand?.name_color,
    nameFont: brand?.name_font,
    nameSize: brand?.name_size,
});

export const Navbar = ({ content }) => {
    const scrolled = useScrolledPast(24);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { open } = useModal();
    const links = content?.nav?.links || [];
    const brand = wordmarkProps(content?.brand);

    const closeMobile = () => setMobileOpen(false);

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.0, ease: EASE, delay: 0.15 }}
            className="fixed top-4 md:top-6 inset-x-0 z-50 px-3 md:px-6 pointer-events-none"
            data-testid="floating-navbar"
        >
            <div
                className={`pointer-events-auto mx-auto max-w-[1240px] flex items-center justify-between transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    scrolled ? "py-2.5 px-4 md:px-5" : "py-3.5 px-5 md:px-7"
                }`}
                style={{
                    background: "rgba(245,245,242,0.72)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(217,215,209,0.55)",
                    borderRadius: 28,
                    boxShadow: scrolled
                        ? "0 18px 40px -22px rgba(0,0,0,0.18)"
                        : "0 10px 30px -22px rgba(0,0,0,0.12)",
                }}
            >
                {/* Desktop: combined wordmark */}
                <a href="#top" className="hidden lg:inline-flex shrink-0" data-testid="nav-brand-link">
                    <Wordmark {...brand} />
                </a>

                {/* Mobile: split logo/text so justify-between centers the text */}
                <a
                    href="#top"
                    className="lg:hidden shrink-0"
                    data-testid="nav-brand-logo-mobile"
                    aria-label={content?.brand?.name || "One Tree Hill"}
                >
                    <Wordmark {...brand} logoOnly />
                </a>
                <a href="#top" className="lg:hidden" data-testid="nav-brand-text-mobile">
                    <Wordmark {...brand} textOnly />
                </a>

                <nav className="hidden lg:flex items-center gap-8 ml-10">
                    {links.map((l) => (
                        <NavLink key={l.label} link={l} variant="desktop" />
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <button
                        onClick={() => open("portal-login")}
                        className="nav-link font-body"
                        data-testid="nav-login-button"
                    >
                        {content?.nav?.login_label || "Login"}
                    </button>
                    <button
                        type="button"
                        onClick={() => open("book")}
                        className="btn-pill btn-pill-dark text-sm"
                        data-testid="nav-cta-button"
                    >
                        {content?.nav?.cta_label || "Book Demo"}
                    </button>
                </div>

                {/* Mobile toggle — pinned right via flex justify-between */}
                <button
                    className="lg:hidden shrink-0 p-2 rounded-full hover:bg-black/5 transition"
                    onClick={() => setMobileOpen((s) => !s)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            <MobileMenu
                open={mobileOpen}
                links={links}
                content={content}
                onNavigate={closeMobile}
                onLogin={() => { closeMobile(); open("portal-login"); }}
                onBook={() => { closeMobile(); open("book"); }}
            />
        </motion.header>
    );
};
